import { supabase } from './supabase';

/**
 * Fidélité côté CLIENT (parité C2 — cagnotte / carte de tampons).
 * S'appuie sur le backend déjà en place (sql/11_loyalty.sql) :
 *   - get_my_loyalty_state(email, tenant_id) → état (tampons / seuil / récompense),
 *   - get_my_loyalty_rewards(email, tenant_id) → récompenses actives (codes), AJOUTÉ par SQL_P3,
 *   - claim_loyalty_stamp(email, slug, amount, first_name) → revendiquer un tampon (QR comptoir).
 * Helpers purs (validation, progression) testables séparément de l'I/O.
 */

export interface LoyaltyState {
  loyaltyEnabled: boolean;
  optedIn: boolean;
  stamps: number;
  threshold: number;
  minCents: number;
  rewardLabel: string | null;
}

export interface LoyaltyReward {
  code: string;
  rewardLabel: string;
  expiresAt: string | null;
}

/** Email plausible (validation légère, pas de RFC complète). */
export function isPlausibleEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Normalise la réponse JSON de get_my_loyalty_state vers un type sûr. Pur. */
export function parseLoyaltyState(json: unknown): LoyaltyState {
  const j = (json ?? {}) as Record<string, unknown>;
  return {
    loyaltyEnabled: j.loyalty_enabled === true,
    optedIn: j.opted_in === true,
    stamps: typeof j.stamps === 'number' ? j.stamps : 0,
    threshold: typeof j.threshold === 'number' ? j.threshold : 10,
    minCents: typeof j.min_cents === 'number' ? j.min_cents : 0,
    rewardLabel: typeof j.reward_label === 'string' ? j.reward_label : null,
  };
}

/** Progression vers la récompense : { current, total, ratio∈[0,1], remaining }. Pur. */
export function stampProgress(state: Pick<LoyaltyState, 'stamps' | 'threshold'>): {
  current: number;
  total: number;
  ratio: number;
  remaining: number;
} {
  const total = Math.max(1, state.threshold);
  const current = Math.max(0, Math.min(state.stamps, total));
  return {
    current,
    total,
    ratio: current / total,
    remaining: Math.max(0, total - current),
  };
}

/** Lit l'état fidélité d'un client (par email) pour un tenant. */
export async function getLoyaltyState(email: string, tenantId: string): Promise<LoyaltyState> {
  const { data, error } = await supabase.rpc('get_my_loyalty_state', {
    p_email: email.trim(),
    p_tenant_id: tenantId,
  });
  if (error) {
    console.warn('[loyalty] get_my_loyalty_state échoué:', error.message);
    return parseLoyaltyState({ loyalty_enabled: true, opted_in: false, stamps: 0 });
  }
  return parseLoyaltyState(data);
}

/**
 * Ajout MANUEL d'un tampon en caisse (parité legacy « Mode caisse fidélité »).
 * Crée le client si inconnu, active l'opt-in, incrémente le tampon, débloque la
 * récompense si le seuil est atteint. Renvoie un message lisible pour la caisse.
 */
export async function addManualStamp(
  email: string,
  tenantId: string,
  amountCents: number,
  firstName?: string,
): Promise<{ ok: boolean; message: string }> {
  if (!isPlausibleEmail(email)) return { ok: false, message: 'Email invalide.' };
  if (!(amountCents > 0)) return { ok: false, message: 'Montant invalide.' };
  const { data, error } = await supabase.rpc('add_manual_loyalty_stamp', {
    p_email: email.trim().toLowerCase(),
    p_tenant_id: tenantId,
    p_amount_cents: amountCents,
    p_first_name: firstName?.trim() || null,
    p_phone: null,
  });
  if (error) return { ok: false, message: error.message };
  const res = (data as { error?: string; stamp_result?: { reward_unlocked?: boolean; stamps?: number; reward_code?: string } } | null) ?? {};
  if (res.error) return { ok: false, message: res.error };
  const sr = res.stamp_result ?? {};
  if (sr.reward_unlocked) return { ok: true, message: `🎉 Récompense débloquée ! Code : ${sr.reward_code ?? ''}` };
  return { ok: true, message: `✓ Tampon ajouté (${sr.stamps ?? '?'} au total).` };
}

/** Liste les récompenses actives d'un client (codes à présenter au comptoir). */
export async function getActiveRewards(email: string, tenantId: string): Promise<LoyaltyReward[]> {
  const { data, error } = await supabase.rpc('get_my_loyalty_rewards', {
    p_email: email.trim(),
    p_tenant_id: tenantId,
  });
  if (error || !Array.isArray(data)) {
    if (error) console.warn('[loyalty] get_my_loyalty_rewards indisponible:', error.message);
    return [];
  }
  return (data as Record<string, unknown>[]).map((r) => ({
    code: String(r.code ?? ''),
    rewardLabel: String(r.reward_label ?? ''),
    expiresAt: typeof r.expires_at === 'string' ? r.expires_at : null,
  }));
}
